import type { TFunction } from 'i18next';
import { SUBSCRIPTION_TIERS } from '../data/subscriptions';
import { visiblePitchTemplates } from '../data/pitchTemplates';
import type { FaqItem } from '../i18n/useHomeFaq';
import {
  CONTACT_EMAILS,
  SITE_NAME,
  absoluteUrl,
  getSiteUrl,
} from './site';

const schemaContext = 'https://schema.org';

export interface StructuredDataContext {
  language: string;
  description: string;
  faq: FaqItem[];
  tiers: Array<{ id: string; name: string; description: string; priceLabel: string }>;
}

export const buildHomeFaqFromTranslations = (t: TFunction): FaqItem[] => {
  const templateNames = visiblePitchTemplates
    .slice(0, 4)
    .map((template) => t(`pitchTemplates.${template.id}.name`, { defaultValue: template.name }))
    .join(', ');

  return [
    {
      question: t('faq.whatIsSpotdove.question'),
      answer: t('faq.whatIsSpotdove.answer'),
    },
    {
      question: t('faq.isFree.question'),
      answer: t('faq.isFree.answer'),
    },
    {
      question: t('faq.templates.question'),
      answer: t('faq.templates.answer', {
        count: visiblePitchTemplates.length,
        names: templateNames,
      }),
    },
    {
      question: t('faq.aiRewrite.question'),
      answer: t('faq.aiRewrite.answer'),
    },
  ];
};

export const buildSubscriptionTiersFromTranslations = (t: TFunction) =>
  SUBSCRIPTION_TIERS.map((tier) => ({
    id: tier.id,
    name: t(`subscription.tiers.${tier.id}.name`, { defaultValue: tier.name }),
    description: t(`subscription.tiers.${tier.id}.description`, { defaultValue: tier.description }),
    priceLabel: t(`subscription.tiers.${tier.id}.price`, { defaultValue: tier.priceLabel }),
  }));

export const buildOrganizationJsonLd = (description: string) => ({
  '@context': schemaContext,
  '@type': 'Organization',
  name: SITE_NAME,
  url: getSiteUrl(),
  description,
  logo: absoluteUrl('/logo.svg'),
  email: CONTACT_EMAILS.legal,
});

export const buildWebSiteJsonLd = (language: string, description: string) => ({
  '@context': schemaContext,
  '@type': 'WebSite',
  name: SITE_NAME,
  url: getSiteUrl(),
  description,
  inLanguage: language,
});

export const buildSoftwareApplicationJsonLd = (
  description: string,
  tiers: StructuredDataContext['tiers'],
) => ({
  '@context': schemaContext,
  '@type': 'SoftwareApplication',
  name: SITE_NAME,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: getSiteUrl(),
  description,
  offers: tiers.map((tier) => ({
    '@type': 'Offer',
    name: `${SITE_NAME} ${tier.name}`,
    description: tier.description,
    price: tier.id === 'free' ? '0' : tier.priceLabel.replace(/[^\d.]/g, '') || '0',
    priceCurrency: 'EUR',
    url: absoluteUrl('/subscription'),
  })),
});

export const buildHomeFaqJsonLd = (faq: FaqItem[]) => ({
  '@context': schemaContext,
  '@type': 'FAQPage',
  mainEntity: faq.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

export const buildPricingJsonLd = (tiers: StructuredDataContext['tiers']) => ({
  '@context': schemaContext,
  '@type': 'Product',
  name: `${SITE_NAME} subscriptions`,
  description: 'Pitch deck builder plans with optional AI features.',
  brand: {
    '@type': 'Brand',
    name: SITE_NAME,
  },
  offers: tiers.map((tier) => ({
    '@type': 'Offer',
    name: tier.name,
    description: tier.description,
    price: tier.id === 'free' ? '0' : tier.priceLabel.replace(/[^\d.]/g, '') || '0',
    priceCurrency: 'EUR',
    url: absoluteUrl('/subscription'),
    availability: 'https://schema.org/InStock',
  })),
});

export const buildHomeJsonLd = ({ language, description, faq, tiers }: StructuredDataContext) => [
  buildOrganizationJsonLd(description),
  buildWebSiteJsonLd(language, description),
  buildSoftwareApplicationJsonLd(description, tiers),
  buildHomeFaqJsonLd(faq),
];

/** @deprecated Use buildHomeJsonLd with translated context instead. */
export const homePageTitle = `${SITE_NAME} — pitch deck builder for founders`;
